import FieldController from '../field.controller';
import { isProvisionalIdentifier, normalizeIdentifier, documentFieldIds, withDocumentFieldIds } from '../../../util';

class FileUploadFieldController extends FieldController {
  constructor(
    $scope,
    $q,
    $translate,
    $log,
    $timeout,
    $window,
    LogService,
    DocumentService
  ) {
    "ngInject";
    super($scope, $q, $translate, $log, $timeout);
    this.$window = $window;
    this.LogService = LogService;
    this.DocumentService = DocumentService;
    this._documents = [];
  }

  $onInit() {
    super.$onInit();
    return this._loadDocuments();
  }

  get canAddNewFile() {
    return this.allowMultipleFiles || this.documents.length === 0;
  }

  canRotateDocument(document) {
    // only allow rotation of newly uploaded files
    return !!isProvisionalIdentifier(document.id);
  }

  documentIsNotImage(document){
    let type = null;
    let output = false;
    if (document.fileDataURL) {
      type = document.fileDataURL.substring(document.fileDataURL.indexOf(":") + 1, document.fileDataURL.indexOf("/"));
    }
    if (type && type !== 'image') {
        output = true;
    }
    return output;
  }

  get allowMultipleFiles() {
    let fieldType = this.fieldInstance
      && this.fieldInstance.field
      && this.fieldInstance.field.fieldType;

    return !!(fieldType && fieldType.isMultipleFileUpload || fieldType.isMultipleImageFileUpload);
  }

  get documents() {
    return this._documents;
  }

  set newFile(file) {
    this._newFile = file;
  }

  get newFile() {
    return this._newFile;
  }

  set documentIds(documentIdList) {
    // MJOL-1826 Use pipe delimiter
    // Tokens that are not document ids, such as an unresolved photo UUID, keep their places.
    this.value = withDocumentFieldIds(this.value, documentIdList);
  }

  get documentIds() {
    // MJOL-1826 Handle both comma and pipe delimiters
    return documentFieldIds(this.value);
  }

  get isImageUpload() {
    let fieldType = this.fieldInstance && this.fieldInstance.field && this.fieldInstance.field.fieldType;
    if (!fieldType) {
      return false;
    }
    return fieldType.isImageFileUpload;
  }

  get inputAcceptType() {
    if (this.isImageUpload) {
      return "image/*,application/pdf";
    } else {
      return "";
    }
  }

  fileChanged($files, $file, $newFiles, $duplicateFiles, $invalidFiles, $event) {
    // Multiple file handler
    if ($files.length > 1 && this.allowMultipleFiles) {
      const MAX_CONCURRENT = 3;
      let active = 0;
      let fileIndex = 0;
      let deferred = this.$q.defer();

      const processNext = () => {
        while (active < MAX_CONCURRENT && fileIndex < $files.length) {
          const file = $files[fileIndex++];
          if (!file || !file.name) continue;

          active++;
          this.DocumentService.createWithLocalFile(file)
            .then(document => this.resizeDocument(document))
            .then(document => {
              this.documents.push(document);
              this.fieldResponse.addDocumentToUpload(document);
              this.newFile = null;
            })
            .catch(err => {
              this.LogService.error("Error creating document:", err);
            })
            .finally(() => {
              active--;
              if (active === 0 && fileIndex >= $files.length) {
                deferred.resolve();
              } else {
                processNext();
              }
            });
        }
      };

      processNext();
      return deferred.promise;
    } else {
      // Single file handler
      return this.DocumentService.createWithLocalFile($file)
        .then(document => {
          return this.resizeDocument(document)
            .then(document => {
              document.events_id = this.fieldResponse.recapResponse.eventId || null;
              this.documents.push(document);
              this.fieldResponse.addDocumentToUpload(document);
              this.newFile = null;
            });
        })
        .catch(err => {
          this.LogService.error("Error creating document:", err);
          return this.$q.reject(err);
        });
    }
  }

  resizeDocument(document) {
    if (!this.canRotateDocument(document)) {
      return this.$q.reject()
    }
    return this.DocumentService.resizeDocument(document)
  }

  removeDocument(document) {
    this.LogService.log("Removing document:", document);

    if (isProvisionalIdentifier(document.id)) {
      // Provisional; remove document from pending uploads
      this.LogService.log("Removing document from pending uploads");
      this.fieldResponse.removeDocumentToUpload(document);
    } else {
      // Already stored on server; remove document ID from field value
      this.LogService.log("Removing document from current ID list");
      this.documentIds = this.documentIds.filter(documentId => documentId !== document.id);

      // Flag document for deletion
      this.fieldResponse.addDocumentToDelete(document);
    }

    // Finally, remove document from our own list
    this._documents = this._documents.filter(currDoc => currDoc.id !== document.id);
  }

  rotateDocumentLeft(document) {
    return this.rotateDocument(document, false);
  }

  rotateDocumentRight(document) {
    return this.rotateDocument(document, true);
  }

  rotateDocument(document, rotateDirectionRight = true) {
    if (!this.canRotateDocument(document)) {
      return this.$q.reject();
    }

    let deferred = this.$q.defer();
    let canvas = this.$window.document.createElement('canvas');
    let img = this.$window.document.createElement('img');
    let type = 'image/jpeg';
    let quality = 0.934;

    img.onload = () => {
      this.LogService.log("Image onload");
      try {
        canvas.width = img.height;
        canvas.height = img.width;

        let ctx = canvas.getContext('2d');

        // Set rotation transform
        if (rotateDirectionRight) {
          ctx.transform(0, 1, -1, 0, img.height, 0);
        } else {
          ctx.transform(0, -1, 1, 0, 0, img.width);
        }

        // Draw image
        ctx.drawImage(img, 0, 0);

        // // Clear context
        // ctx.clearRect(0, 0, canvas.width, canvas.height);
        //
        // // Translate canvas so we will rotate around center point
        // ctx.translate(canvas.width / 2, canvas.height / 2);
        //
        // // Do 90 degree rotation
        // ctx.rotate((Math.PI / 180) * rotateDegrees);
        //
        // // Translate canvas back to origin
        // ctx.translate(-canvas.width / 2, -canvas.height / 2);

        // Retrieve new data URL
        document.fileDataURL = canvas.toDataURL(type, quality);
        deferred.resolve(document);
      } catch (e) {
        return deferred.reject(e);
      }
    };

    img.onerror = () => {
      deferred.reject();
    };

    this.LogService.log("Attempting to load image");
    img.src = document.fileDataURL;

    return deferred.promise
      .then(document => {
        this.LogService.log("Successfully rotated document");
        return document;
      })
      .catch(err => {
        this.LogService.warn("Error rotating document:", err);
        return this.$q.reject(err);
      });
  }

  _loadDocuments() {
    this.documentIds = this.documentIds.filter(documentID => isNaN(documentID) != true);

    let promises = this.documentIds.map(documentId => this.DocumentService.getThinById(documentId));
    return this.$q.all(promises)
      .then(documents => {

        // Documents contains all thin documents referenced by the field response's value.
        // We need to append any pending uploads and remove any pending deletions

        // First we'll remove any documents whose ID's match those pending upload or deletion
        // Then we'll append documents pending upload

        let pendingUpload = this.fieldResponse.documentsToUpload;
        let pendingDelete = this.fieldResponse.documentsToDelete;

        // First build a hash for quick lookup of document ID's that are pending upload or deletion
        let documentIdsPendingUploadOrDelete = pendingUpload.concat(pendingDelete)
          .reduce((hash, document) => {
            hash[normalizeIdentifier(document.id)] = true;
            return hash;
          }, {});

        // Now filter documents to not include any of these pending documents
        documents = documents.filter(document => !documentIdsPendingUploadOrDelete[document.id]);

        // Finally append all documents pending upload
        documents = documents.concat(pendingUpload);

        this._documents = documents;
      })
  }
}

export default FileUploadFieldController;
